'use client';

import React, { useMemo } from 'react';
import { ResponsiveContainer, Sankey, Tooltip as RechartsTooltip, Layer, Rectangle } from 'recharts';
import { cn } from '@/lib/utils';

/**
 * Per-edge session flow as returned by the funnel analytics backend.
 * `sessions` = distinct sessions that visited BOTH source and target step.
 */
export interface FunnelFlow {
    id: string;
    source: string;
    target: string;
    condition: string | null;
    label: string;
    sessions: number;
}

// ── Branch colour mapping ────────────────────────────────
// Semantic where we can (accepted = green, declined = neutral, failed = red),
// then cycle a small categorical palette for any extra branches.
const CATEGORICAL = [
    { hex: '#3b82f6', bar: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
    { hex: '#8b5cf6', bar: 'bg-violet-500', text: 'text-violet-600 dark:text-violet-400' },
    { hex: '#06b6d4', bar: 'bg-cyan-500', text: 'text-cyan-600 dark:text-cyan-400' },
    { hex: '#f97316', bar: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400' },
] as const;

const EMERALD = { hex: '#10b981', bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' };
const SLATE = { hex: '#64748b', bar: 'bg-slate-400', text: 'text-slate-600 dark:text-slate-300' };
const ROSE = { hex: '#f43f5e', bar: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400' };

export function branchColor(label: string, index: number): { hex: string; bar: string; text: string } {
    const l = (label || '').toLowerCase();
    if (l.includes('accept') || l.includes('paid') || l.includes('subscrib')) return EMERALD;
    if (l.includes('declin') || l.includes('ignore') || l.includes('default')) return SLATE;
    if (l.includes('fail') || l.includes('reject') || l.includes('error')) return ROSE;
    return CATEGORICAL[index % CATEGORICAL.length];
}

const EXIT_HEX = '#f43f5e';

// ============================================================
// SEGMENTED BRANCH BAR ("offshoot" of the waterfall spine)
// ============================================================

interface BranchBreakdownProps {
    /** Distinct sessions that reached the branching step. */
    visitors: number;
    /** Outgoing flows for this step (already filtered to this source). */
    flows: FunnelFlow[];
    /** Resolve a step id to its display name (for the segment legend). */
    stepName: (stepId: string) => string;
    dense?: boolean;
}

/**
 * 100%-stacked bar that splits a branching step's visitors into each outgoing
 * path (e.g. Accepted / Declined) plus a distinct "Exited" segment for the true
 * drop-off. This keeps intentional path choice visually separate from churn.
 */
export function BranchBreakdown({ visitors, flows, stepName, dense }: BranchBreakdownProps) {
    const segments = useMemo(() => {
        const sorted = [...flows].sort((a, b) => b.sessions - a.sessions);
        const taken = sorted.reduce((sum, f) => sum + f.sessions, 0);
        // Flows and step visitors are counted by separate queries, so a skew in
        // either would push the segments past a full bar. Widening the base to
        // whichever is larger keeps this a true 100%-stacked bar regardless.
        const base100 = Math.max(visitors, taken);
        const exited = Math.max(0, visitors - taken);
        const base = sorted.map((f, i) => {
            const color = branchColor(f.label, i);
            return {
                key: f.id,
                label: f.label,
                target: stepName(f.target),
                sessions: f.sessions,
                pct: base100 > 0 ? (f.sessions / base100) * 100 : 0,
                hex: color.hex,
                bar: color.bar,
                text: color.text,
                isExit: false,
            };
        });
        if (exited > 0) {
            base.push({
                key: '__exit__',
                label: 'Exited',
                target: '',
                sessions: exited,
                pct: base100 > 0 ? (exited / base100) * 100 : 0,
                hex: EXIT_HEX,
                bar: 'bg-rose-500/50',
                text: 'text-rose-600 dark:text-rose-400',
                isExit: true,
            });
        }
        return base;
    }, [flows, visitors, stepName]);

    if (visitors <= 0 || segments.length === 0) return null;

    return (
        <div className={cn('ml-7 border-l-2 border-dashed border-border pl-3', dense ? 'mt-0.5 pb-1' : 'mt-1 pb-1.5')}>
            {/* Stacked bar */}
            <div className={cn('flex w-full overflow-hidden rounded-full bg-muted', dense ? 'h-2' : 'h-2.5')}>
                {segments.map((s) => (
                    <div
                        key={s.key}
                        className={cn(
                            s.bar,
                            s.isExit && 'bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(244,63,94,0.25)_3px,rgba(244,63,94,0.25)_6px)]',
                        )}
                        style={{ width: `${Math.max(s.pct, s.sessions > 0 ? 1.5 : 0)}%` }}
                        title={`${s.label}${s.target ? ` → ${s.target}` : ''}: ${s.sessions.toLocaleString()} (${s.pct.toFixed(0)}%)`}
                    />
                ))}
            </div>
            {/* Legend */}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                {segments.map((s) => (
                    <span key={s.key} className="flex items-center gap-1 text-[10px]">
                        <span className={cn('h-2 w-2 flex-shrink-0 rounded-sm', s.bar)} />
                        <span className="font-medium text-foreground">{s.label}</span>
                        <span className={cn('font-bold tabular-nums', s.text)}>{s.pct.toFixed(0)}%</span>
                        <span className="tabular-nums text-muted-foreground">({s.sessions.toLocaleString()})</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

// ============================================================
// SANKEY FLOW DIAGRAM (advanced)
// ============================================================

interface SankeyStep {
    id: string;
    name: string;
}

interface FunnelSankeyProps {
    steps: SankeyStep[];
    flows: FunnelFlow[];
    height?: number;
}

interface SankeyNodeDatum {
    name: string;
    /** Short label rendered on the canvas. */
    shortLabel?: string;
}
interface SankeyLinkDatum {
    source: number;
    target: number;
    value: number;
    kind: 'continue' | 'branch';
    color: string;
    label?: string;
    sourceName?: string;
    targetName?: string;
}

// Main-flow (single continuation) ribbon colour — calm brand indigo so the
// dominant conversion path reads first, and only true branches get semantics.
const FLOW_HEX = '#6366f1';

/**
 * Recharts Sankey of the whole funnel. The dominant continuation path is a calm
 * indigo ribbon and true branch points (accept/decline/…) get semantic colours,
 * so the conversion structure reads at a glance even with 15+ steps. Drop-off is
 * intentionally NOT drawn as ribbons here: recharts right-aligns every sink, so
 * per-step exits would span the whole canvas and wash out the flow. Exact churn
 * lives in the Steps view; this view is about *where people go*.
 */
export function FunnelSankey({ steps, flows, height = 460 }: FunnelSankeyProps) {
    const data = useMemo(() => {
        const positiveFlows = flows.filter((f) => f.sessions > 0);

        const used = new Set<string>();
        for (const f of positiveFlows) {
            used.add(f.source);
            used.add(f.target);
        }

        // Group outgoing flows per source to tell continuation from a real branch.
        const outBySource: Record<string, FunnelFlow[]> = {};
        for (const f of positiveFlows) {
            (outBySource[f.source] ||= []).push(f);
        }

        // Build real step nodes in funnel order.
        const orderedUsed = steps.filter((s) => used.has(s.id));
        const indexById: Record<string, number> = {};
        const nodes: SankeyNodeDatum[] = [];
        orderedUsed.forEach((s, i) => {
            indexById[s.id] = nodes.length;
            nodes.push({ name: s.name, shortLabel: `${i + 1}. ${s.name}` });
        });

        const links: SankeyLinkDatum[] = [];

        // Flow links: continuation vs branch.
        for (const [source, outs] of Object.entries(outBySource)) {
            const isBranch = outs.length > 1;
            const sorted = [...outs].sort((a, b) => b.sessions - a.sessions);
            sorted.forEach((f, i) => {
                if (indexById[f.source] == null || indexById[f.target] == null) return;
                links.push({
                    source: indexById[f.source],
                    target: indexById[f.target],
                    value: f.sessions,
                    kind: isBranch ? 'branch' : 'continue',
                    color: isBranch ? branchColor(f.label, i).hex : FLOW_HEX,
                    label: isBranch ? f.label : 'Continue',
                    sourceName: nodes[indexById[f.source]]?.name,
                    targetName: nodes[indexById[f.target]]?.name,
                });
            });
        }

        return { nodes, links };
    }, [steps, flows]);

    if (data.links.length === 0) {
        return (
            <div className="flex h-40 items-center justify-center text-xs text-muted-foreground">
                No branching flow to display for this period.
            </div>
        );
    }

    return (
        <div>
            <ResponsiveContainer width="100%" height={height}>
                <Sankey
                    data={data}
                    nodePadding={22}
                    nodeWidth={10}
                    linkCurvature={0.5}
                    iterations={96}
                    margin={{ top: 16, right: 16, bottom: 28, left: 18 }}
                    node={<SankeyNode />}
                    link={<SankeyLink />}
                >
                    <RechartsTooltip
                        content={<SankeyTooltip />}
                        wrapperStyle={{ outline: 'none' }}
                    />
                </Sankey>
            </ResponsiveContainer>
            {/* Legend */}
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                    <span className="h-2 w-4 rounded-sm" style={{ backgroundColor: FLOW_HEX, opacity: 0.55 }} />
                    Continues
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="h-2 w-4 rounded-sm" style={{ backgroundColor: EMERALD.hex, opacity: 0.55 }} />
                    Accepted
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="h-2 w-4 rounded-sm" style={{ backgroundColor: SLATE.hex, opacity: 0.55 }} />
                    Declined
                </span>
                <span className="text-muted-foreground/70">Drop-off shown in Steps view</span>
            </div>
        </div>
    );
}

// Custom node: a thin bar with a vertical step label to its left and the reached
// count beneath it. Vertical labels keep dense funnels (15+ steps) readable.
function SankeyNode(props: any) {
    const { x, y, width, height, index, payload } = props;
    const total = payload?.value;
    const h = Math.max(height, 2);
    // With 15+ tightly packed columns, horizontal labels collide no matter where
    // you anchor them. Vertical labels each own their column, so they never
    // overlap — this is how dense Sankeys stay readable.
    const raw = (payload?.shortLabel ?? payload?.name ?? '') as string;
    const label = raw.length > 24 ? `${raw.slice(0, 23)}…` : raw;
    const cx = x + width / 2;
    const vy = y + h / 2;
    const lx = x - 3;
    return (
        <Layer key={`node-${index}`}>
            <Rectangle x={x} y={y} width={width} height={h} fill="#475569" fillOpacity={0.9} radius={2 as any} />
            <text
                x={lx}
                y={vy}
                textAnchor="middle"
                transform={`rotate(-90, ${lx}, ${vy})`}
                className="fill-foreground"
                fontSize={10}
                fontWeight={600}
            >
                {label}
            </text>
            {total != null && (
                <text x={cx} y={y + h + 12} textAnchor="middle" className="fill-muted-foreground" fontSize={9} fontWeight={500}>
                    {Number(total).toLocaleString()}
                </text>
            )}
        </Layer>
    );
}

// Custom link: continuation = indigo, branch = semantic colour. Brightens on hover.
function SankeyLink(props: any) {
    const {
        sourceX, targetX, sourceY, targetY,
        sourceControlX, targetControlX, linkWidth, index, payload,
    } = props;
    const color = payload?.color ?? FLOW_HEX;
    return (
        <path
            key={`link-${index}`}
            d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
            fill="none"
            stroke={color}
            strokeOpacity={0.42}
            strokeWidth={Math.max(linkWidth, 1)}
            style={{ transition: 'stroke-opacity 0.2s' }}
            onMouseEnter={(e) => (e.currentTarget.style.strokeOpacity = '0.7')}
            onMouseLeave={(e) => (e.currentTarget.style.strokeOpacity = '0.42')}
        />
    );
}

function SankeyTooltip({ active, payload }: any) {
    if (!active || !payload || payload.length === 0) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    // Link payloads carry source/target node indices; node payloads carry name.
    const isLink = p.source != null && p.target != null && p.payload;
    if (isLink) {
        const link = p.payload as SankeyLinkDatum;
        const value = p.value ?? link?.value ?? 0;
        return (
            <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
                <div className="font-semibold">{`${link?.sourceName ?? ''} → ${link?.targetName ?? ''}`}</div>
                {link?.kind === 'branch' && link.label && (
                    <div className="text-[11px] text-muted-foreground">{link.label}</div>
                )}
                <div className="tabular-nums text-muted-foreground">{Number(value).toLocaleString()} sessions</div>
            </div>
        );
    }
    return (
        <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
            <div className="font-semibold">{p.name}</div>
            {p.value != null && (
                <div className="tabular-nums text-muted-foreground">{Number(p.value).toLocaleString()} sessions reached</div>
            )}
        </div>
    );
}
